import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { VideoPlayerMainHandlers } from '@/components/Video/hooks/useVideoPlayerHandlers';
import { DEFAULT_LOGO_SORT_ORDER } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/constants';
import { getLogoFromItem } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/helpers/batchSelectionHelpers';
import { useRowExpandOverrides } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/hooks/useRowExpandOverrides';
import {
  GROUP_OPTION_VALUES,
  GroupingPresencesCriteria,
  InlineDialogCriteria,
  THUMBNAIL_SIZE_DEFAULT,
  VISUALIZATION_OPTIONS_VALUES,
  VisualizationOption,
} from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/types';
import { ESegmentToolkit } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/types';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { flattenCollection } from '@/features/broadcast-schedule-program/helpers/flatten-collection';
import { getPresenceAtTime } from '@/features/broadcast-schedule-program/helpers/get-presence-at-time';
import { getPreviousPresenceById } from '@/features/broadcast-schedule-program/helpers/get-previous-presence-by-id';
import { isPresenceFormOnScreen } from '@/features/broadcast-schedule-program/helpers/is-presence-form-on-screen';
import { TClipLogo } from '@/features/clips/types';
import { TFeaturedPeopleType } from '@/features/featured-people/types';
import { TLogo } from '@/features/logos/types';
import { readStoredLogosLabelsFilter } from '@/features/logos/utils/labels-filter-storage';
import { useGetSupports } from '@/features/programs/api/get-supports';
import { getSupportAliasById } from '@/features/programs/helpers/supports';
import { TClipPresence, TSegmentLogo, TSegmentPresence } from '@/features/segments/types';
import { SortOrder } from '@/types';
import { TPresenceItem } from '@/types/presences';
import { convertDatesToSeconds } from '@/utils/hh-mm-ss-into-number';
import { titleCase } from '@/utils/strings';

export type GroupedPresencesType = (TPresenceItem | { id: any; children: TPresenceItem[] })[];
export type ResolvedPresenceEntities = {
  logosById: ReadonlyMap<string, TLogo>;
  peopleById: ReadonlyMap<string, TFeaturedPeopleType>;
};
type GoToFn = (presence: TPresenceItem, currentTime: number, seekVideo?: boolean) => void;

export function useBatchSelection() {
  const { t } = useTranslation();
  const {
    activeSegment,
    activeClip,
    presenceData,
    pinnedId,
    formBox,
    isGlobalView,
    goToPresence,
    goToClipPresence,
    clearPresence,
  } = useBroadcastScheduleProgram();
  const isPeopleTab = pinnedId === ESegmentToolkit.PERSONAS;
  // While a logo/person form is on screen, navigating would refill it with another
  // presence or turn an edit into a duplicate (see use-segment-presences).
  const isPresenceNavigationSuspended = isPresenceFormOnScreen(formBox, pinnedId, isGlobalView);

  // Clear stale presence data when the user switches tabs (pinnedId changes).
  // Without this, presenceData from the previous tab (e.g. a logoId while on People)
  // causes handleGoNextBase to exit early (id not found in the new tab's groupedPresences),
  // breaking the autoplay/navigation feature until a manual seek resets the state.
  useEffect(() => {
    clearPresence && clearPresence();
  }, [pinnedId]);

  const [selectedPresencesIds, setSelectedPresencesIds] = useState<string[]>([]);
  const [expandAllPresences, setExpandAllPresences] = useState<boolean>(false);
  const tableFiltersRef = useRef<Record<string, string | string[]>>({
    labels: readStoredLogosLabelsFilter(),
  });
  const [currentPresenceDrawer, setCurrentPresenceDrawer] = useState<TPresenceItem | null>(null);
  const [isAutoRevealsMode, setAutoRevealsMode] = useState(true);
  const [groupedPresences, setGroupedPresences] = useState<GroupedPresencesType>([]);
  const [isAutoplayingClips, setAutoplayingClips] = useState<boolean>(false);
  const [dropdownCoords, setDropdownCoords] = useState<{
    top: number;
    left: number;
    right: number;
  } | null>(null);
  const [openInline, setOpenInline] = useState<{
    presence?: string;
    support?: string;
    visibility?: string;
    reliability?: string;
  }>({});

  const toggleInline = (type: InlineDialogCriteria, id: string) => {
    setOpenInline(prev => {
      const isOpen = prev[type] === id;

      if (isOpen) {
        return {};
      }

      return { [type]: id };
    });
  };

  const [groupPresenceCriteria, setGroupPresenceCriteria] = useState<GroupingPresencesCriteria>(
    GROUP_OPTION_VALUES.Presence
  );
  const [sortDirection, setSortDirection] = useState<SortOrder>(
    DEFAULT_LOGO_SORT_ORDER as SortOrder
  );
  const [groupVisualization, setGroupVisualization] = useState<VisualizationOption>(
    VISUALIZATION_OPTIONS_VALUES.List
  );
  const [thumbnailSize, setThumbnailSize] = useState<number>(THUMBNAIL_SIZE_DEFAULT);

  const {
    overrides: rowExpandOverrides,
    setOverride: setRowExpandOverride,
    clearOverrides: clearRowExpandOverrides,
    getDesired: getRowExpandDesired,
  } = useRowExpandOverrides();

  const onChangeVisualization = (visualization: string) => {
    setGroupVisualization(visualization as VisualizationOption);
  };

  const { data: allSupports = [] } = useGetSupports({ config: { enabled: false } });
  const hasSelectedPresences = selectedPresencesIds.length > 0;

  /**
   * The provider wraps every review page, so it resolves nothing itself: an answer here would
   * re-render the whole page. The caller passes the logos and people it has resolved.
   */
  const getItemParentTitle = <T extends TSegmentLogo | TClipLogo>(
    item: T,
    { logosById, peopleById }: ResolvedPresenceEntities
  ) => {
    const { id, children } = item;

    if (id && children) {
      switch (groupPresenceCriteria) {
        case GROUP_OPTION_VALUES.Reliability:
          return id as string;
        case GROUP_OPTION_VALUES.Visibility:
          return titleCase(id);
        case GROUP_OPTION_VALUES.Support:
          return getSupportAliasById(id, allSupports) || t('Sin definir');
        case GROUP_OPTION_VALUES.Presence:
        default:
          const logo = getLogoFromItem(item, logosById);
          if (logo?.name) return logo.name;
          const firstChild = (item as any).children?.[0];
          const peopleId = firstChild?.people_id;
          if (peopleId) {
            const person = peopleById.get(peopleId);
            return (
              [person?.name, person?.surname].filter(Boolean).join(' ') ||
              firstChild.entity_label ||
              ''
            );
          }
          return item.entity_label || firstChild?.entity_label || '';
      }
    }

    return '';
  };

  const setDropdownCoordsFromRect = (rect: DOMRect) => {
    setDropdownCoords({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
      right: rect.right + window.scrollX,
    });
  };

  const handleEndPresence = (handlers: VideoPlayerMainHandlers) => {
    if (isPresenceNavigationSuspended) return;

    if (!isAutoRevealsMode) {
      handlers.handlePause();
    }

    if (isAutoplayingClips) {
      const { id } = presenceData || {};
      if (!id) return;

      const flattenPresencesCollection = flattenCollection(
        groupedPresences as TPresenceItem[]
      ).sort((a, b) => (a.start_datetime < b.start_datetime ? 1 : -1));
      const previousPresence = getPreviousPresenceById(id, flattenPresencesCollection);

      if (previousPresence) {
        if (activeClip) {
          const previousPresenceStartSeconds = Math.abs(
            convertDatesToSeconds(activeClip.start_datetime, previousPresence.start_datetime)
          );
          goToClipPresence(previousPresence as TClipPresence, previousPresenceStartSeconds);
        } else if (activeSegment) {
          const previousPresenceStartSeconds = Math.abs(
            convertDatesToSeconds(activeSegment.start_datetime, previousPresence.start_datetime)
          );
          goToPresence(previousPresence as TSegmentPresence, previousPresenceStartSeconds);
        }
      }
    }
  };

  const handleGoNextBase = (
    playedSeconds: number,
    handlers: VideoPlayerMainHandlers,
    startDatetime: string | Date | undefined,
    goTo: GoToFn,
    extraCondition: boolean = true
  ) => {
    if (isPresenceNavigationSuspended || !extraCondition || !startDatetime) {
      return;
    }

    const flattenPresencesCollection = flattenCollection(groupedPresences as TPresenceItem[]).sort(
      (a, b) => (a.start_datetime < b.start_datetime ? 1 : -1)
    );

    let targetPresence: TPresenceItem | null = null;
    let targetStartSeconds: number = Infinity;

    if (presenceData) {
      // Normal forward-playback path: advance from the current presence to the next one.
      const { id = '' } = presenceData;
      targetPresence = getPreviousPresenceById(id, flattenPresencesCollection);
      if (!targetPresence) return;
      targetStartSeconds = Math.abs(
        convertDatesToSeconds(startDatetime, targetPresence.start_datetime)
      );
    } else {
      // Fallback path: presenceData was cleared (e.g. after a seek that landed between
      // presences). Scan the flat list (sorted descending) for the chronologically-next
      // presence whose start has been reached by the current playback position.
      // "Next" = smallest startSeconds that is >= playedSeconds in ascending order,
      // which is the *last* item in the descending list that satisfies startSeconds <= playedSeconds
      // (i.e. the one we should have transitioned into).
      //
      // We collect all presences that have already started (startSeconds <= playedSeconds)
      // and pick the one with the largest startSeconds (= most recently started = last entered).
      let bestStart = -1;
      for (const presence of flattenPresencesCollection) {
        const s = Math.abs(convertDatesToSeconds(startDatetime, presence.start_datetime));
        if (s <= playedSeconds && s > bestStart) {
          bestStart = s;
          targetPresence = presence;
          targetStartSeconds = s;
        }
      }
      if (!targetPresence) return;
    }

    // console.info('Time to next presence: ', playedSeconds, ' *** ', targetStartSeconds);

    if (playedSeconds >= targetStartSeconds) {
      // seekVideo=false: the video is already at the correct position (playing or post-seek);
      // we only update the table selection and bbox overlay without jumping the progress bar.
      // targetStartSeconds is used as the frame reference so checkPresence computes
      // relativePlayedSeconds correctly (playedSeconds - presenceStart).
      goTo(targetPresence, targetStartSeconds, false);
      void handlers;
    }
  };

  const handleGoNextPresence = (playedSeconds: number, handlers: VideoPlayerMainHandlers) => {
    handleGoNextBase(
      playedSeconds,
      handlers,
      activeSegment?.start_datetime,
      goToPresence as GoToFn
    );
  };

  const handleGoNextClipPresence = (playedSeconds: number, handlers: VideoPlayerMainHandlers) => {
    handleGoNextBase(
      playedSeconds,
      handlers,
      activeClip?.start_datetime,
      goToClipPresence as GoToFn,
      Boolean(activeClip)
    );
  };

  /**
   * Called when the user manually seeks the video progress bar (segment player).
   * Searches directly in the segment's own presence arrays so the result is always
   * consistent regardless of which toolkit table is currently mounted or what
   * `groupedPresences` contains at the time of the seek.
   */
  const handleSeekPresence = (seekedSeconds: number) => {
    if (isPresenceNavigationSuspended || !activeSegment?.start_datetime) return;

    // Search only within the active tab's presence array. Using `presenceMode`
    // (set by useGroupedPresences on mount) is more reliable than inspecting
    // groupedPresences, which updates asynchronously and could be stale during
    // a tab switch. This prevents setting the wrong presence type (e.g. logoData
    // when the People table is mounted), which would break row highlighting and
    // forward navigation (handleGoNextBase looks for the id inside groupedPresences).
    const allSegmentPresences: TPresenceItem[] = isPeopleTab
      ? (activeSegment.segment_peoples ?? [])
      : (activeSegment.segment_logos ?? []);

    const presence = getPresenceAtTime(
      seekedSeconds,
      activeSegment.start_datetime,
      allSegmentPresences
    );

    // Always clear first so checkNextTrigger is unblocked when landing
    // outside any presence window.
    clearPresence();

    if (presence) {
      const startSeconds = Math.abs(
        convertDatesToSeconds(activeSegment.start_datetime, presence.start_datetime)
      );
      // seekVideo=false: the user already positioned the video via the progress bar;
      // we only need to update the bbox overlay, not jump the video to presence start.
      goToPresence(presence as TSegmentPresence, startSeconds, false);
    }
  };

  /**
   * Called when the user manually seeks the video progress bar (clip player).
   * Same rationale as handleSeekPresence — uses the clip's own arrays directly.
   */
  const handleSeekClipPresence = (seekedSeconds: number) => {
    if (isPresenceNavigationSuspended || !activeClip?.start_datetime) return;

    // Same rationale as handleSeekPresence — use presenceMode instead of groupedPresences.
    const allClipPresences: TPresenceItem[] = isPeopleTab
      ? (activeClip.clip_peoples ?? [])
      : (activeClip.clip_logos ?? []);

    const presence = getPresenceAtTime(seekedSeconds, activeClip.start_datetime, allClipPresences);

    clearPresence();

    if (presence) {
      const startSeconds = Math.abs(
        convertDatesToSeconds(activeClip.start_datetime, presence.start_datetime)
      );
      // seekVideo=false: same rationale as handleSeekPresence.
      goToClipPresence(presence as TClipPresence, startSeconds, false);
    }
  };

  return {
    selectedPresencesIds,
    setSelectedPresencesIds,
    hasSelectedPresences,
    toggleInline,
    openInline,
    groupPresenceCriteria,
    setGroupPresenceCriteria,
    sortDirection,
    setSortDirection,
    dropdownCoords,
    setDropdownCoordsFromRect,
    getItemParentTitle,
    currentPresenceDrawer,
    setCurrentPresenceDrawer,
    isAutoRevealsMode,
    setAutoRevealsMode,
    setGroupedPresences,
    groupedPresences,
    isAutoplayingClips,
    setAutoplayingClips,
    expandAllPresences,
    setExpandAllPresences,
    groupVisualization,
    onChangeVisualization,
    thumbnailSize,
    setThumbnailSize,
    tableFiltersRef,
    handleEndPresence,
    handleGoNextPresence,
    handleGoNextClipPresence,
    handleSeekPresence,
    handleSeekClipPresence,
    rowExpandOverrides,
    setRowExpandOverride,
    clearRowExpandOverrides,
    getRowExpandDesired,
  };
}
